import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import Login from "./Login";
import {
  Dashboard, ReleasesAdmin, AnalyticsAdmin, CampaignsAdmin, SpotifySync, FanList, SettingsAdmin,
} from "./sections";
import { CampaignPagesAdmin } from "./campaigns";
import { VideosAdmin, NewsAdmin, EngagementAdmin } from "./content";
import { PromoterDashboard } from "./promoter";
import {
  LayoutDashboard, Disc3, BarChart3, Megaphone, RefreshCw, Users, Settings as SettingsIcon, LogOut, Link2, Video, Newspaper, Vote, Rocket,
} from "lucide-react";

const TABS = [
  { id: "promoter", label: "Promoter", Icon: Rocket, C: PromoterDashboard },
  { id: "dashboard", label: "Player Stats", Icon: LayoutDashboard, C: Dashboard },
  { id: "campaign-pages", label: "Campaign Pages", Icon: Link2, C: CampaignPagesAdmin },
  { id: "releases", label: "Releases", Icon: Disc3, C: ReleasesAdmin },
  { id: "videos", label: "Videos", Icon: Video, C: VideosAdmin },
  { id: "news", label: "News", Icon: Newspaper, C: NewsAdmin },
  { id: "engage", label: "Fan Vote & Social", Icon: Vote, C: EngagementAdmin },
  { id: "analytics", label: "Analytics", Icon: BarChart3, C: AnalyticsAdmin },
  { id: "campaigns", label: "UTM Performance", Icon: Megaphone, C: CampaignsAdmin },
  { id: "sync", label: "Spotify Sync", Icon: RefreshCw, C: SpotifySync },
  { id: "fans", label: "Fan List", Icon: Users, C: FanList },
  { id: "settings", label: "Settings", Icon: SettingsIcon, C: SettingsAdmin },
];

export default function AdminApp() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("promoter");

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-gold border-t-transparent" />
      </div>
    );
  }
  if (!user) return <Login />;
  if (!user.isAdmin) {
    return (
      <div className="grain flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="font-display text-2xl font-bold uppercase text-white">Not authorized</p>
        <p className="text-sm text-white/50">{user.email} does not have admin access.</p>
        <button onClick={logout} className="rounded-full bg-gold px-6 py-3 text-sm font-bold uppercase text-black">Sign out</button>
      </div>
    );
  }

  const Active = TABS.find((t) => t.id === tab)?.C || Dashboard;

  return (
    <div className="grain relative min-h-screen bg-ink lg:flex">
      {/* Sidebar */}
      <aside className="glass sticky top-0 z-30 flex items-center gap-1 overflow-x-auto border-b border-white/10 px-3 py-3 lg:h-screen lg:w-64 lg:flex-col lg:items-stretch lg:border-b-0 lg:border-r lg:px-4 lg:py-6">
        <div className="hidden px-2 lg:mb-6 lg:block">
          <p className="font-display text-xl font-bold uppercase text-white">DJ <span className="text-gold">Código</span></p>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Admin</p>
        </div>
        {TABS.map(({ id, label, Icon }) => (
          <button
            key={id}
            data-testid={`tab-${id}`}
            onClick={() => setTab(id)}
            className={`flex shrink-0 items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${tab === id ? "bg-gold text-black" : "text-white/60 hover:text-white"}`}
          >
            <Icon size={17} /> <span className="whitespace-nowrap">{label}</span>
          </button>
        ))}
        <button data-testid="admin-logout" onClick={logout} className="mt-auto hidden items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium text-white/50 hover:text-red-300 lg:flex">
          <LogOut size={17} /> Sign out
        </button>
      </aside>

      {/* Content */}
      <main className="flex-1 px-5 py-8 sm:px-8 lg:px-12">
        <div className="mx-auto max-w-5xl">
          <Active />
        </div>
      </main>
    </div>
  );
}
